'use client';

import { useCallback, useEffect, useRef } from 'react';
import logSponsoredListCarouselDisplayed from '@alltrails/amplitude/events/logSponsoredListCarouselDisplayed';
import Typography from '@alltrails/core/components/Typography';
import useIntersectionObserver from '@alltrails/core/hooks/useIntersectionObserver';
import Carousel from '@alltrails/denali/components/Carousel';
import type { SponsoredCollection } from '../../../utils/types';
import SponsoredListCard from './SponsoredListCard';
import styles from './styles.module.scss';

type Props = {
  title: string;
  collections: SponsoredCollection[];
};

const getItemKey = (collection: SponsoredCollection) => collection.slug;

const SponsoredLists = ({ title, collections }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const renderItem = useCallback(
    (collection: SponsoredCollection, index: number) => (
      <div className={styles.cardContainer} key={collection.slug}>
        <SponsoredListCard collection={collection} carouselIndex={index} carouselLength={collections.length} />
      </div>
    ),
    [collections.length]
  );
  const [, shouldTrackView] = useIntersectionObserver(containerRef);

  useEffect(() => {
    if (shouldTrackView) {
      logSponsoredListCarouselDisplayed({
        carousel_length: collections.length,
        location: 'web home page sponsored list carousel',
      });
    }
  }, [shouldTrackView, collections.length]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.riverContainer}>
        <Typography variant="heading400" component="h2" className={styles.sponsoredListTitle}>
          {title}
        </Typography>
        <Carousel
          items={collections}
          renderItem={renderItem}
          getItemKey={getItemKey}
          className={styles.carousel}
          leftArrowClassName={styles.leftArrow}
          rightArrowClassName={styles.rightArrow}
          absoluteSizing={true}
          slideAsSet
          showPaginationDots={false}
          showOverflow={false}
        />
      </div>
    </div>
  );
};

export default SponsoredLists;
